import React, { useState, useEffect } from 'react';
import Image from 'gatsby-plugin-sanity-image';
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
} from 'framer-motion';
import useEmblaCarousel from 'embla-carousel-react';
import { useGesture } from 'react-use-gesture';

import { BlockWrapper } from '../blockWrapper';
import { useScrollFreeze } from '@hooks';

import CloseIcon from '@svg/close.svg';
import Chevron from '@svg/chevron.svg';

const lightVars = {
  open: {
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: [0.43, 0.13, 0.23, 0.96],
    },
  },
  closed: {
    opacity: 0,
    transition: {
      duration: 0.5,
      ease: [0.43, 0.13, 0.23, 0.96],
    },
  },
};

const cursorVars = {
  visible: {
    scale: 1,
    opacity: 1,
  },
  dragging: {
    scale: 0.5,
    opacity: 1,
  },
  hidden: {
    scale: 0.2,
    opacity: 0,
  },
};

const cursorTextVars = {
  visible: {
    opacity: 1,
  },
  dragging: {
    opacity: 0,
  },
  hidden: {
    opacity: 0,
  },
};

const CarouselImage = ({ ...item }) => {
  console.log(item);
  return (
    <div
      className='w-full h-full flex flex-row items-center justify-center'
      style={{ flex: '0 0 auto' }}>
      <div className='w-11/12 lg:w-2/3'>
        {item && <Image {...item.figureImage} className='w-full' />}
      </div>
    </div>
  );
};

const LightInner = ({ toggleLight, images, index }) => {
  useScrollFreeze();

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    dragFree: false,
    containScroll: true,
    startIndex: index,
  });

  return (
    <>
      <motion.div
        variants={lightVars}
        initial='initial'
        animate='animate'
        exit='initial'
        className='overflow-hidden fixed z-50 top-0 left-0 right-0 bottom-0 w-screen h-full bg-black bg-opacity-80 flex items-center justify-center h-full text-white'>
        <button
          onClick={toggleLight}
          className='absolute right-gutter lg:right-halfGutter top-halfGutter lighthouse-close'>
          <CloseIcon className='w-10 lg:w-16' />
        </button>

        <div ref={emblaRef} className='w-full h-full'>
          <div className='flex w-full gap-10 lg:gap-6 h-full absolute left-0 top-0'>
            {images.map((item, i) => (
              <CarouselImage key={i} {...item} />
            ))}
          </div>
        </div>

        <button
          onClick={() => emblaApi.scrollNext()}
          className='focus:outline-none hidden lg:block absolute right-halfGutter w-10 h-full transform -rotate-90'>
          <svg
            viewBox='0 0 16 11'
            fill='white'
            xmlns='http://www.w3.org/2000/svg'>
            <path d='M1.88 0.453125L8 6.55979L14.12 0.453125L16 2.33312L8 10.3331L0 2.33312L1.88 0.453125Z' />
          </svg>
        </button>
        <button
          onClick={() => emblaApi.scrollPrev()}
          className='focus:outline-none hidden lg:block absolute left-halfGutter w-10 h-full transform rotate-90'>
          <svg
            viewBox='0 0 16 11'
            fill='white'
            xmlns='http://www.w3.org/2000/svg'>
            <path d='M1.88 0.453125L8 6.55979L14.12 0.453125L16 2.33312L8 10.3331L0 2.33312L1.88 0.453125Z' />
          </svg>
        </button>
      </motion.div>
    </>
  );
};

const Lighthouse = ({ lightOpen, toggleLight, images, index }) => {
  return (
    <AnimatePresence exitBeforeEnter>
      {lightOpen && (
        <LightInner
          key={lightOpen}
          toggleLight={toggleLight}
          images={images}
          index={index}
        />
      )}
    </AnimatePresence>
  );
};

const ImageGrid = ({ images, blockConfig }) => {
  const [lightOpen, setLightOpen] = useState(false);
  const toggleLight = () => setLightOpen((prev) => !prev);

  const [index, setIndex] = useState(0);

  return (
    <>
      <BlockWrapper {...blockConfig} className='grid grid-cols-14'>
        <div className='col-start-2 col-end-14 grid grid-cols-1 lg:grid-cols-3 lg:gap-x-16 gap-y-8 lg:gap-y-32'>
          {images.map((img, i) => {
            const { title, caption, altTag, figureImage } = img || {};
            return (
              <div className='flex flex-col'>
                <div
                  className='overflow-hidden cursor-pointer'
                  key={i}
                  onClick={() => {
                    setIndex(i);
                    setLightOpen(true);
                  }}>
                  <Image
                    className='object-cover top-0 left-0 hover:scale-105 transform duration-300 ease-in-out'
                    {...figureImage}
                  />
                </div>
                <div className='prose md:prose-lg mt-6'>
                  <h5 className='font-medium'>{title}</h5>
                  <p>{caption}</p>
                </div>
              </div>
            );
          })}
        </div>
      </BlockWrapper>

      <AnimatePresence exitBeforeEnter>
        <Lighthouse
          lightOpen={lightOpen}
          toggleLight={toggleLight}
          images={images}
          index={index}
        />
      </AnimatePresence>
    </>
  );
};

export default ImageGrid;
